/**
 * Settings → Points of Entry
 * Switch a PoE active/inactive and set the go-live date of upcoming (inactive) ones.
 * Active here = available in the agent app, WhatsApp and web screening.
 * The PDX outgestion feed (GET /data-outgestion/poes) lists active PoEs, plus
 * inactive ones with a go-live date as "upcoming".
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  IconButton,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import api from '../../services/api';

interface EntryPoint {
  id: number;
  code: string;
  title: string;
  type: string;
  country_code: string;
  is_active: boolean;
  go_live_date: string | null;
  updated_at: string | null;
}

const TYPE_LABEL: Record<string, string> = {
  'Air-Port': 'Airport',
  'Sea-Port': 'Sea port',
  'Land-Port': 'Land border',
  'Railway-Port': 'Railway',
};

const PoeSettingsTab: React.FC = () => {
  const [rows, setRows] = useState<EntryPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/entry-points-admin');
      setRows(res.data.entry_points || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load points of entry');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (ep: EntryPoint, body: Record<string, any>, message: string) => {
    setSavingId(ep.id);
    try {
      const res = await api.patch(`/entry-points-admin/${ep.id}`, body);
      setRows(prev => prev.map(r => (r.id === ep.id ? res.data : r)));
      setSnack(message);
    } catch (e: any) {
      setSnack(e?.response?.data?.detail || 'Update failed');
    } finally {
      setSavingId(null);
    }
  };

  const onToggle = (ep: EntryPoint, checked: boolean) =>
    save(ep, { is_active: checked }, `${ep.title} is now ${checked ? 'active' : 'inactive'}`);

  const onDate = (ep: EntryPoint, value: string) => {
    if ((ep.go_live_date || '') === value) return;
    save(
      ep,
      value ? { go_live_date: value } : { clear_go_live_date: true },
      value ? `Go-live date saved for ${ep.title}` : `Go-live date cleared for ${ep.title}`,
    );
  };

  const activeCount = rows.filter(r => r.is_active).length;

  return (
    <Card>
      <CardHeader
        title="Points of Entry"
        subheader={`${activeCount} active of ${rows.length}. Active points of entry appear in the agent app, WhatsApp and web screening. The PDX feed shares active points of entry, and inactive ones as upcoming once a go-live date is set.`}
        action={
          <Tooltip title="Reload">
            <span>
              <IconButton onClick={load} disabled={loading}>
                <Refresh />
              </IconButton>
            </span>
          </Tooltip>
        }
      />
      <CardContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Point of Entry</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Active</TableCell>
                  <TableCell>Go-live date</TableCell>
                  <TableCell>PDX feed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(ep => (
                  <TableRow key={ep.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{ep.code}</TableCell>
                    <TableCell>{ep.title}</TableCell>
                    <TableCell>{TYPE_LABEL[ep.type] || ep.type}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={ep.is_active ? 'Active' : 'Inactive'}
                        color={ep.is_active ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={ep.is_active}
                        disabled={savingId === ep.id}
                        onChange={e => onToggle(ep, e.target.checked)}
                      />
                    </TableCell>
                    <TableCell>
                      {ep.is_active ? (
                        <Typography variant="body2" color="text.secondary">Live</Typography>
                      ) : (
                        <TextField
                          key={`${ep.id}-${ep.go_live_date || ''}`}
                          type="date"
                          size="small"
                          defaultValue={ep.go_live_date || ''}
                          disabled={savingId === ep.id}
                          onBlur={e => onDate(ep, e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {ep.is_active ? (
                        <Chip size="small" color="success" variant="outlined" label="Shared · active" />
                      ) : ep.go_live_date ? (
                        <Chip size="small" color="primary" variant="outlined" label="Shared · upcoming" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">Not shared</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack('')} message={snack} />
    </Card>
  );
};

export default PoeSettingsTab;
